/**
 * A widget's "⋯" menu, as in AgencyAnalytics: Edit, Full screen, Copy,
 * Duplicate, Move to page…, Remove. Plus the full-screen view itself, which
 * read-only dashboards and the client portal also open (the magnifier).
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import type { CatalogueSource, Widget, WidgetResult } from './reports-api.ts';
import { WidgetView } from './WidgetView.tsx';

export interface WidgetMenuActions {
  onEdit: () => void;
  onFullScreen: () => void;
  onCopy: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
  /** Other pages it can move to; empty hides "Move to page". */
  pages: { index: number; title: string }[];
  onMoveTo: (pageIndex: number) => void;
  /** Saves the widget's numbers as XLSX; absent for widgets without data. */
  onDownload?: (() => void) | undefined;
}

export function WidgetMenu(actions: WidgetMenuActions): ReactNode {
  const [open, setOpen] = useState(false);
  const [moving, setMoving] = useState(false);
  const box = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event: Event) => {
      if (
        event instanceof KeyboardEvent
          ? event.key === 'Escape'
          : !box.current?.contains(event.target as Node)
      ) {
        setOpen(false);
        setMoving(false);
      }
    };
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', close);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', close);
    };
  }, [open]);
  const run = (action: () => void) => () => {
    setOpen(false);
    setMoving(false);
    action();
  };
  return (
    <div ref={box} className="rwm" onPointerDown={(event) => event.stopPropagation()}>
      <button
        type="button"
        className="rwm-button"
        aria-label="Widget options"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        ⋯
      </button>
      {open && (
        <div className="rwm-menu" role="menu">
          {moving ? (
            <>
              <button
                type="button"
                role="menuitem"
                className="rwm-back"
                onClick={() => setMoving(false)}
              >
                ‹ Move to page
              </button>
              {actions.pages.map((page) => (
                <button
                  type="button"
                  role="menuitem"
                  key={page.index}
                  onClick={run(() => actions.onMoveTo(page.index))}
                >
                  {page.title}
                </button>
              ))}
            </>
          ) : (
            <>
              <button type="button" role="menuitem" onClick={run(actions.onEdit)}>
                Edit
              </button>
              <button type="button" role="menuitem" onClick={run(actions.onFullScreen)}>
                Full screen
              </button>
              <button type="button" role="menuitem" onClick={run(actions.onCopy)}>
                Copy
              </button>
              <button type="button" role="menuitem" onClick={run(actions.onDuplicate)}>
                Duplicate
              </button>
              {actions.onDownload && (
                <button type="button" role="menuitem" onClick={run(actions.onDownload)}>
                  Download XLS
                </button>
              )}
              {actions.pages.length > 0 && (
                <button type="button" role="menuitem" onClick={() => setMoving(true)}>
                  Move to page…
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                className="rwm-danger"
                onClick={run(actions.onRemove)}
              >
                Remove
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function FullScreenWidget({
  widget,
  result,
  source,
  onClose,
}: {
  widget: Widget;
  result: WidgetResult | undefined;
  source: CatalogueSource | undefined;
  onClose: () => void;
}): ReactNode {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const height = Math.round(window.innerHeight * 0.7);
  // Rendered at the document root: a scaled slide (Present mode) would otherwise trap it.
  return createPortal(
    <div
      className="rc-scrim"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div
        className="rwf"
        role="dialog"
        aria-modal="true"
        aria-label="Widget, full screen"
        style={{ height }}
      >
        <button
          type="button"
          className="rb-icon rwf-close"
          aria-label="Close full screen"
          onClick={onClose}
        >
          ×
        </button>
        <WidgetView widget={widget} result={result} source={source} height={height - 32} />
      </div>
    </div>,
    document.body,
  );
}
